import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  GetTasksDataTableDocument,
  ProjectionKind,
  TasksValueObjectsTaskIdentifierEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/datatable.graphql'
import { TasksTaskStatusEnum } from 'generated/resources/graphql'

import { dateBackendDateTimeToUI, dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TaskStatus from 'modules/tasks/components/TaskStatus'
import { TaskAssignee } from 'modules/tasks/components/TasksList/types'

import AssigneesList from '../../AssigneesList'

type sortableFields = 'dueOn' | 'name' | 'createdAt' | undefined

export interface TasksFiltersType {
  search: string
  status?: TasksTaskStatusEnum
  authorId?: number
}

const NameProjection = () => {
  const sortBy: sortableFields = 'name'

  return {
    identifier: 'name',
    colSpan: 6,
    template: '',
    sortBy: sortBy,
    kind: ProjectionKind.Custom,
    title: i18n.t('tasks.labels.name'),
    render: ({ name, completedAt }: { name: string; completedAt: Date }) => {
      return (
        <Box flexDirection="row" alignItems="center" gap="s8">
          <Text.Normal lineClamp={2} stroked={!!completedAt} weight="medium">
            {name}
          </Text.Normal>
        </Box>
      )
    },
  }
}

const BelongsToProjection = () => {
  return {
    identifier: 'belongsTo',
    colSpan: 6,
    template: '',
    kind: ProjectionKind.Custom,
    title: i18n.t('tasks.labels.belongs_to'),
    render: ({ relatedResourceTitle }: { relatedResourceTitle?: string | null }) => {
      return (
        <Box flexDirection="row" alignItems="center" gap="s8">
          <Text.Normal lineClamp={2} weight="medium">
            {relatedResourceTitle}
          </Text.Normal>
        </Box>
      )
    },
  }
}

const AssigneeProjection = () => ({
  identifier: 'assigneesConnection',
  colSpan: 3,
  kind: ProjectionKind.Custom,
  title: i18n.t('tasks.labels.assignees'),
  template: '',
  render: ({
    assigneesConnection,
  }: {
    assigneesConnection: { edges?: { node: TaskAssignee }[] | null; totalCount?: number | null }
  }) => {
    const totalCount = assigneesConnection.totalCount ?? 0
    const assignees = assigneesConnection?.edges?.map((edge) => edge.node) ?? []

    return <AssigneesList assignees={assignees} count={totalCount} />
  },
})

const AffecteeProjection = () => ({
  identifier: 'affectees',
  colSpan: 3,
  kind: ProjectionKind.Custom,
  title: i18n.t('tasks.labels.affected_employees'),
  template: '',
  render: ({ affectees }: { affectees: TaskAssignee[] }) => {
    return (
      <Box minWidth="s140">
        {affectees.length > 1 ? (
          <AssigneesList assignees={affectees} />
        ) : (
          <Text.Normal lineClamp={2} weight="medium">
            {affectees[0]?.fullName}
          </Text.Normal>
        )}
      </Box>
    )
  },
})

const ProjectStatus = () => ({
  identifier: 'status',
  colSpan: 4,
  kind: ProjectionKind.Custom,
  title: i18n.t('tasks.labels.status'),
  template: '',
  render: ({
    id,
    identifier,
    status,
  }: {
    id: number
    identifier: TasksValueObjectsTaskIdentifierEnum
    status: TasksValueObjectsTaskStatusEnum | null | undefined
  }) => {
    if (!status) return '-'
    const refetchQueries = [
      { query: GetTasksDataTableDocument, variables: { ids: [id], identifier } },
    ]
    return (
      <Box onClick={(event: MouseEvent) => event.preventDefault()} minWidth="s140">
        <TaskStatus
          taskId={id}
          status={status}
          identifier={identifier}
          refetchQueries={refetchQueries}
        />
      </Box>
    )
  },
})

const TaskDates = () => ({
  kind: ProjectionKind.Custom,
  identifier: 'dates',
  title: i18n.t('tasks.labels.dates'),
  render: ({
    startsOn,
    dueOn,
  }: {
    startsOn: string | null | undefined
    dueOn: string | null | undefined
  }) => {
    const startsOnLabel = startsOn ? dateBackendToUI(startsOn) : '-'
    const dueOnLabel = dueOn ? dateBackendDateTimeToUI(dueOn) : '-'
    return (
      <Box minWidth="s200" flexDirection="row" gap="s12" alignItems="center">
        <Box width="s80" textAlign="center" justifyContent="center" alignItems="center">
          <Text.Normal size="200">{startsOnLabel}</Text.Normal>
        </Box>
        <Icon.Small icon="arrow-right" color="grey800" />
        <Box width="s80" textAlign="center" justifyContent="center" alignItems="center">
          <Text.Normal size="200">{dueOnLabel}</Text.Normal>
        </Box>
      </Box>
    )
  },
  template: '{{task.startAt}}',
  colSpan: 3,
})

export const projections = () => {
  return [
    NameProjection(),
    AffecteeProjection(),
    BelongsToProjection(),
    AssigneeProjection(),
    ProjectStatus(),
    TaskDates(),
  ]
}
